class ProductBundle extends HTMLElement {
  constructor() {
    super();
    this.bundleItems = new Map();
    this.minProducts = parseInt(this.dataset.min) || 2;
    this.storageKey = `product-bundle-items-${this.id || 'default'}`;
    this.cart = null;
    this.moneyFormat = this.dataset.moneyFormat || null;
    this.currencyCodeEnabled = this.dataset.currencyCodeEnabled === 'true';
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    // Clean up if needed
  }

  init() {
    const cartBehavior = window.themeSettings?.cartBehavior || 'drawer';
    if (cartBehavior === 'drawer') {
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    } else {
      this.cart = null;
    }
    this.loadFromStorage();
    this.refreshBundleItemsFromVariants();
    this.initializeVariantSelections();
    this.bindEvents();
    this.updateSummary();
  }

  initializeVariantSelections() {
    this.querySelectorAll('[data-component="bundle-product-card"]').forEach(card => {
      const variantsContainer = card.querySelector('[data-component="bundle-variants"]');
      if (!variantsContainer) return;

      const optionGroups = variantsContainer.querySelectorAll('.bundle-variants__group');
      optionGroups.forEach(group => {
        const swatches = group.querySelectorAll('.bundle-variant-swatch');
        const hasSelection = Array.from(swatches).some(s => s.classList.contains('bundle-variant-swatch--selected'));
        
        if (!hasSelection && swatches.length > 0) {
          const firstAvailable = Array.from(swatches).find(s => !s.classList.contains('bundle-variant-swatch--disabled'));
          const firstSwatch = firstAvailable || swatches[0];
          if (firstSwatch) {
            firstSwatch.classList.add('bundle-variant-swatch--selected');
          }
        }
      });

      this.updateVariantAvailability(card);
    });
  }

  bindEvents() {
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-component="bundle-add-button"]')) {
        this.handleAddToBundle(e);
      }
    });

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-component="bundle-remove-button"]')) {
        this.handleRemoveFromBundle(e);
      }
    });

    this.addEventListener('click', (e) => {
      if (e.target.closest('.bundle-variant-swatch')) {
        this.handleVariantSelection(e);
      }
    });

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-component="bundle-item-remove"]')) {
        this.handleRemoveItem(e);
      }
    });

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-component="bundle-summary"] .button')) {
        this.handleAddBundleToCart(e);
      }
    });

    this.addEventListener('click', (e) => {
      if (e.target.closest('.bundle-summary__title')) {
        this.toggleSummaryVisibility();
      }
    });
  }

  showBundleError(message) {
    const err = this.querySelector('[data-component="bundle-error"]');
    if (!err) return;
    const text = err.querySelector('.bundle-summary__error-text');
    if (text) text.textContent = message || '';
    err.classList.remove('hidden');
    const summary = this.querySelector('[data-component="bundle-summary"]');
    if (summary) summary.classList.remove('bundle-hidden');
  }

  hideBundleError() {
    const err = this.querySelector('[data-component="bundle-error"]');
    if (!err) return;
    err.classList.add('hidden');
    const text = err.querySelector('.bundle-summary__error-text');
    if (text) text.textContent = '';
  }

  handleAddToBundle(e) {
    const card = e.target.closest('[data-component="bundle-product-card"]');
    if (!card) return;

    const productId = card.dataset.productId;
    const productHandle = card.dataset.productHandle;
    
    if (productId === 'placeholder') return;

    const maxProducts = parseInt(this.dataset.max) || 6;
    if (this.bundleItems.size >= maxProducts) {
      console.warn(`Bundle is full! Maximum ${maxProducts} products allowed.`);
      return;
    }

    const selectedVariant = this.getSelectedVariant(card);
    if (!selectedVariant) return;

    this.addToBundle(productId, productHandle, selectedVariant);
    
    this.updateCardState(card, true);
    this.updateSummary();
    this.saveToStorage();
    this.updateAddButtonStates();
    
    this.dispatchEvent(new CustomEvent('bundle:item-added', {
      detail: { productId, variant: selectedVariant }
    }));
  }

  handleRemoveFromBundle(e) {
    const card = e.target.closest('[data-component="bundle-product-card"]');
    if (!card) return;

    const productId = card.dataset.productId;
    
    this.removeFromBundle(productId);
    
    this.updateCardState(card, false);
    this.updateSummary();
    this.saveToStorage();
    
    this.updateAddButtonStates();
    
    this.dispatchEvent(new CustomEvent('bundle:item-removed', {
      detail: { productId }
    }));
  }

  handleVariantSelection(e) {
    const swatch = e.target.closest('.bundle-variant-swatch');
    if (!swatch) return;

    const card = swatch.closest('[data-component="bundle-product-card"]');
    if (!card) return;

    const optionType = swatch.dataset.optionType;
    const optionValue = swatch.dataset.optionValue;


    if (optionType) {
      card.querySelectorAll(`[data-option-type="${optionType}"]`).forEach(s => {
        s.classList.remove('bundle-variant-swatch--selected');
      });
    } else {
      card.querySelectorAll('.bundle-variant-swatch').forEach(s => {
        s.classList.remove('bundle-variant-swatch--selected');
      });
    }
    swatch.classList.add('bundle-variant-swatch--selected');

    this.updateVariantAvailability(card);

    const selectedOptions = this.getSelectedOptions(card);
    
    let variant = this.findVariantByOptions(card, selectedOptions);
    
    if (!variant || !variant.available) {
      variant = this.findFirstAvailableVariant(card, selectedOptions);
      
      if (variant) {
        this.selectVariantOptions(card, variant);
      }
    }
    
    if (!variant) {
      console.warn('❌ No variant found for selected options:', selectedOptions);
      return;
    }

    this.updateCardWithVariant(card, variant);

    const productId = card.dataset.productId;
    if (this.bundleItems.has(productId)) {
      this.bundleItems.set(productId, {
        ...this.bundleItems.get(productId),
        variantId: variant.id,
        price: variant.price,
        priceFormatted: variant.price_formatted || null,
        variantTitle: variant.title,
        image: variant.featured_image || card.dataset.productImage,
        available: variant.available
      });

      this.updateSummary();
      this.saveToStorage();
    }
  }

  getSelectedOptions(card) {
    const variantsContainer = card.querySelector('[data-component="bundle-variants"]');
    if (!variantsContainer) return [null, null, null];

    const selectedOptions = [null, null, null];
    
    const selectedSwatches = variantsContainer.querySelectorAll('.bundle-variant-swatch--selected');
    
    selectedSwatches.forEach(swatch => {
      const optionType = swatch.dataset.optionType;
      const optionValue = swatch.dataset.optionValue;
      
      if (optionType === 'default') {
        selectedOptions[0] = optionValue === 'default' ? null : optionValue;
      } else {
        const allSwatchesForType = variantsContainer.querySelectorAll(`[data-option-type="${optionType}"]`);
        if (allSwatchesForType.length > 0) {
          const firstSwatch = allSwatchesForType[0];
          const swatchesGroup = firstSwatch.closest('.bundle-variants__group');
          const allGroups = variantsContainer.querySelectorAll('.bundle-variants__group');
          const position = Array.from(allGroups).indexOf(swatchesGroup);
          
          if (position >= 0 && position < 3) {
            selectedOptions[position] = optionValue;
          }
        }
      }
    });
    
    return selectedOptions;
  }

  findVariantByOptions(card, selectedOptions) {
    const productId = card.dataset.productId;
    const variantsScript = document.querySelector(`[data-product-variants="${productId}"]`);
    
    if (!variantsScript) {
      console.warn('No variants data found for product:', productId);
      return null;
    }

    try {
      const variantsData = JSON.parse(variantsScript.textContent);
      
      const matchingVariant = variantsData.variants.find(variant => {
        return selectedOptions.every((selectedOption, index) => {
          const variantOption = variant[`option${index + 1}`];
          
          if (selectedOption === null) return true;
          
          return variantOption === selectedOption;
        });
      });

      return matchingVariant || null;
    } catch (error) {
      console.error('Error parsing variants data:', error);
      return null;
    }
  }

  updateCardWithVariant(card, variant) {
    const priceElement = card.querySelector('.card__price');
    if (priceElement && variant.price !== undefined) {
      const formattedPrice = variant.price_formatted || this.formatPrice(variant.price);
      priceElement.innerHTML = formattedPrice;
    }

    this.updateCardImage(card, variant);

    const addButton = card.querySelector('[data-component="bundle-add-button"] .button');
    const removeButton = card.querySelector('[data-component="bundle-remove-button"] .button');
    
    if (variant.available === false) {
      if (addButton) {
        addButton.disabled = true;
        addButton.setAttribute('aria-label', 'Out of stock');
      }
      card.classList.add('variant-unavailable');
    } else {
      if (addButton && !this.bundleItems.has(card.dataset.productId)) {
        addButton.disabled = false;
        addButton.setAttribute('aria-label', 'Add to bundle');
      }
      card.classList.remove('variant-unavailable');
    }

    this.updateAddButtonStates();
  }

  updateCardImage(card, variant) {
    const imageElement = card.querySelector('[data-component="card-image"]');
    const mediaContainer = card.querySelector('.card__media');
    
    if (!mediaContainer) return;

    let imageUrl = null;
    let imageSource = 'none';
    
    if (variant && variant.featured_image) {
      imageUrl = variant.featured_image;
      imageSource = 'variant';
    }
    
    if (!imageUrl) {
      imageUrl = card.dataset.productImage;
      if (imageUrl) {
        imageSource = 'product';
      }
    }

    if (imageUrl && imageUrl !== '') {
      if (imageElement) {
        const sourceUrl = this.normalizeShopifyImageUrl(imageUrl);
        if (!sourceUrl) return;

        imageElement.srcset = this.buildShopifySrcset(sourceUrl);
        imageElement.src = this.buildShopifyImageUrl(sourceUrl, 633);
        imageElement.style.display = '';
      }
      
      const placeholder = mediaContainer.querySelector('.card__media-placeholder');
      if (placeholder) {
        placeholder.style.display = 'none';
      }
    } else {
      
      if (imageElement) {
        imageElement.style.display = 'none';
      }
      
      let placeholder = mediaContainer.querySelector('.card__media-placeholder');
      if (!placeholder) {
        placeholder = document.createElement('div');
        placeholder.className = 'card__media-placeholder';
        placeholder.innerHTML = '<svg class="placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525 525"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path><path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-24c1-1.2 2.9-1.2 3.8 0l8.9 10.7 20.7-24.9c1-1.2 2.9-1.2 3.8 0l29.6 35.5v7.9H212.2v-5.2zm-1.8-67.3h105v10H210.4v-10z"></path></svg>';
        mediaContainer.appendChild(placeholder);
      }
      placeholder.style.display = '';
    }
  }

  normalizeShopifyImageUrl(imageUrl) {
    if (!imageUrl || typeof imageUrl !== 'string') return null;

    try {
      const parsedUrl = new URL(imageUrl, window.location.origin);
      parsedUrl.searchParams.delete('width');
      return parsedUrl.toString();
    } catch (error) {
      return imageUrl;
    }
  }

  buildShopifyImageUrl(baseUrl, width) {
    try {
      const parsedUrl = new URL(baseUrl, window.location.origin);
      parsedUrl.searchParams.set('width', String(width));
      return parsedUrl.toString();
    } catch (error) {
      return baseUrl;
    }
  }

  buildShopifySrcset(baseUrl) {
    const widths = [140, 165, 180, 320, 340, 360, 380, 420, 460, 500, 533, 720, 940, 1066];

    return widths
      .map(width => `${this.buildShopifyImageUrl(baseUrl, width)} ${width}w`)
      .join(', ');
  }

  updateVariantAvailability(card) {
    const productId = card.dataset.productId;
    const variantsScript = document.querySelector(`[data-product-variants="${productId}"]`);
    
    if (!variantsScript) return;

    try {
      const variantsData = JSON.parse(variantsScript.textContent);
      const variantsContainer = card.querySelector('[data-component="bundle-variants"]');
      if (!variantsContainer) return;

      const optionGroups = variantsContainer.querySelectorAll('.bundle-variants__group');
      if (optionGroups.length === 0) return;

      const currentSelections = this.getSelectedOptions(card);

      optionGroups.forEach((group, groupIndex) => {
        const swatches = group.querySelectorAll('.bundle-variant-swatch');
        
        swatches.forEach(swatch => {
          const optionValue = swatch.dataset.optionValue;
          
          let hasAvailableVariant;
          
          if (groupIndex === 0) {
            hasAvailableVariant = variantsData.variants.some(variant => {
              return variant[`option${groupIndex + 1}`] === optionValue && variant.available;
            });
          } 
          else {
            hasAvailableVariant = variantsData.variants.some(variant => {
              if (variant[`option${groupIndex + 1}`] !== optionValue) return false;
              
              if (!variant.available) return false;
              
              return currentSelections.every((selectedValue, index) => {
                if (index === groupIndex) return true;
                if (selectedValue === null) return true;
                return variant[`option${index + 1}`] === selectedValue;
              });
            });
          }

          if (hasAvailableVariant) {
            swatch.classList.remove('bundle-variant-swatch--disabled');
            swatch.setAttribute('aria-disabled', 'false');
          } else {
            swatch.classList.add('bundle-variant-swatch--disabled');
            swatch.setAttribute('aria-disabled', 'true');
          }
        });
      });
    } catch (error) {
      console.error('Error updating variant availability:', error);
    }
  }

  findFirstAvailableVariant(card, selectedOptions) {
    const productId = card.dataset.productId;
    const variantsScript = document.querySelector(`[data-product-variants="${productId}"]`);
    
    if (!variantsScript) return null;

    try {
      const variantsData = JSON.parse(variantsScript.textContent);
      
      const availableVariants = variantsData.variants.filter(v => v.available);
      
      if (availableVariants.length === 0) return null;

      let bestMatch = availableVariants[0];
      let bestMatchScore = 0;

      availableVariants.forEach(variant => {
        let matchScore = 0;
        
        if (selectedOptions[0] && variant.option1 === selectedOptions[0]) {
          matchScore += 100;
        }
        if (selectedOptions[1] && variant.option2 === selectedOptions[1]) {
          matchScore += 10;
        }
        if (selectedOptions[2] && variant.option3 === selectedOptions[2]) {
          matchScore += 1;
        }

        if (matchScore > bestMatchScore) {
          bestMatchScore = matchScore;
          bestMatch = variant;
        }
      });

      return bestMatch;
    } catch (error) {
      console.error('Error finding available variant:', error);
      return null;
    }
  }

  selectVariantOptions(card, variant) {
    const variantsContainer = card.querySelector('[data-component="bundle-variants"]');
    if (!variantsContainer) return;

    const optionGroups = variantsContainer.querySelectorAll('.bundle-variants__group');
    
    optionGroups.forEach((group, index) => {
      const optionValue = variant[`option${index + 1}`];
      if (!optionValue) return;

      const swatches = group.querySelectorAll('.bundle-variant-swatch');
      swatches.forEach(swatch => {
        if (swatch.dataset.optionValue === optionValue) {
          swatches.forEach(s => s.classList.remove('bundle-variant-swatch--selected'));
          swatch.classList.add('bundle-variant-swatch--selected');
        }
      });
    });

    this.updateVariantAvailability(card);
  }

  handleRemoveItem(e) {
    const item = e.target.closest('[data-bundle-item]');
    if (!item) return;

    const productId = item.dataset.productId;
    this.removeFromBundle(productId);
    
    this.updateAllCardStates();
    
    this.updateSummary();
    this.saveToStorage();
    
    this.dispatchEvent(new CustomEvent('bundle:item-removed', {
      detail: { productId }
    }));
  }

  async handleAddBundleToCart(e) {
    e.preventDefault();
    
    if (this.bundleItems.size === 0) {
      console.warn('Please select at least one product for your bundle.');
      return;
    }

    if (this.bundleItems.size < this.minProducts) {
      console.warn(`Please select at least ${this.minProducts} products for your bundle.`);
      return;
    }

    const button = e.target.closest('.button');
    if (!button) return;

    const buttonText = button.querySelector('.button__text');
    const originalText = buttonText ? buttonText.textContent : button.textContent;
    
    try {
      if (buttonText) {
        buttonText.textContent = 'Adding...';
      } else {
        button.textContent = 'Adding...';
      }
      button.disabled = true;

      const items = Array.from(this.bundleItems.values()).map(item => ({
        id: item.variantId,
        quantity: 1
      }));

      const payload = { items };
      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        if (typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(button);
        }
      }

      const response = await fetch(`${window.Shopify?.routes?.root || window.routes?.root_url || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();

      if (responseData.status || !response.ok) {
        if (this.isSellingPlanRequiredError(responseData)) {
          return;
        }
        let msg = typeof responseData.description === 'string' ? responseData.description : responseData.message;
        if (!msg && responseData.description && typeof responseData.description === 'object') {
          const first = Object.values(responseData.description).flat().find((s) => typeof s === 'string' && s.trim());
          msg = first ? first.trim() : null;
        }
        throw new Error(msg || this.dataset.errorAddToCart || 'Could not add bundle to cart. Please try again.');
      }

      if (response.ok) {
        this.bundleItems.clear();
        this.hideBundleError();
        this.updateSummary();
        this.saveToStorage();
        this.updateAllCardStates();
        
        this.dispatchEvent(new CustomEvent('bundle:added-to-cart', {
          detail: { items: Array.from(this.bundleItems.values()) }
        }));
        
        if (this.cart && typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(responseData);
        } else {
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }
      } else {
        throw new Error(this.dataset.errorAddToCart || 'Could not add bundle to cart.');
      }
    } catch (error) {
      this.showBundleError(error.message || this.dataset.errorAddToCart);
    } finally {
      if (buttonText) {
        buttonText.textContent = originalText;
      } else {
        button.textContent = originalText;
      }
      button.disabled = false;
    }
  }

  isSellingPlanRequiredError(response) {
    if (!response) return false;

    const hasSellingPlanKey =
      (response.errors && typeof response.errors === 'object' && 'selling_plan' in response.errors) ||
      (response.description && typeof response.description === 'object' && 'selling_plan' in response.description);

    const message = [
      typeof response.message === 'string' ? response.message : '',
      typeof response.description === 'string' ? response.description : '',
    ]
      .join(' ')
      .toLowerCase();

    const hasSellingPlanText = message.includes('selling plan') || message.includes('subscription');

    return Boolean(hasSellingPlanKey || hasSellingPlanText);
  }

  addToBundle(productId, productHandle, variant) {
    const validPrice = parseFloat(variant.price) || 0;
    
    this.bundleItems.set(productId, {
      productId,
      productHandle,
      variantId: variant.id,
      title: variant.title,
      price: validPrice,
      priceFormatted: variant.priceFormatted || variant.price_formatted || null,
      variantTitle: variant.variantTitle,
      image: variant.image,
      productImage: variant.productImage,
      available: variant.available
    });
  }

  removeFromBundle(productId) {
    this.bundleItems.delete(productId);
  }

  getSelectedVariant(card) {
    const productId = card.dataset.productId;
    const productTitle = card.dataset.productTitle || '';
    const productImage = card.dataset.productImage || '';
    
    const selectedOptions = this.getSelectedOptions(card);
    
    const variant = this.findVariantByOptions(card, selectedOptions);
    
    if (variant) {
      const variantTitle = variant.title && variant.title !== 'Default Title' && variant.title.trim() !== '' 
        ? variant.title 
        : '';
      
      return {
        id: variant.id,
        title: productTitle,
        price: variant.price,
        priceFormatted: variant.price_formatted || null,
        variantTitle: variantTitle,
        image: variant.featured_image || productImage,
        productImage: productImage,
        available: variant.available
      };
    }
    
    const variantsScript = document.querySelector(`[data-product-variants="${productId}"]`);
    if (variantsScript) {
      try {
        const variantsData = JSON.parse(variantsScript.textContent);
        const firstVariant = variantsData.variants[0];
        
        if (firstVariant) {
          const variantTitle = firstVariant.title && firstVariant.title !== 'Default Title' && firstVariant.title.trim() !== '' 
            ? firstVariant.title 
            : '';
          
          return {
            id: firstVariant.id,
            title: productTitle,
            price: firstVariant.price,
            priceFormatted: firstVariant.price_formatted || null,
            variantTitle: variantTitle,
            image: firstVariant.featured_image || productImage,
            productImage: productImage,
            available: firstVariant.available
          };
        }
      } catch (error) {
        console.error('Error getting variant data:', error);
      }
    }
    
    return {
      id: productId,
      title: productTitle,
      price: 0,
      variantTitle: '',
      image: productImage,
      productImage: productImage,
      available: true
    };
  }

  updateCardState(card, isSelected) {
    const addButton = card.querySelector('[data-component="bundle-add-button"]');
    const removeButton = card.querySelector('[data-component="bundle-remove-button"]');
    
    if (addButton && removeButton) {
      if (isSelected) {
        card.classList.add('bundle-product-card--selected');
        addButton.style.display = 'none';
        removeButton.style.display = 'flex';
      } else {
        card.classList.remove('bundle-product-card--selected');
        addButton.style.display = 'flex';
        removeButton.style.display = 'none';
      }
    }
  }

  updateAllCardStates() {
    this.querySelectorAll('[data-component="bundle-product-card"]').forEach(card => {
      const productId = card.dataset.productId;
      const isSelected = this.bundleItems.has(productId);
      this.updateCardState(card, isSelected);
    });
    
    document.querySelectorAll(`[data-product-id]:not([data-product-id="placeholder"])`).forEach(card => {
      const productId = card.dataset.productId;
      if (productId && productId !== 'placeholder') {
        const isSelected = this.bundleItems.has(productId);
        const addButton = card.querySelector('[data-component="bundle-add-button"]');
        const removeButton = card.querySelector('[data-component="bundle-remove-button"]');
        
        if (addButton && removeButton) {
          if (isSelected) {
            card.classList.add('bundle-product-card--selected');
            addButton.style.display = 'none';
            removeButton.style.display = 'flex';
          } else {
            card.classList.remove('bundle-product-card--selected');
            addButton.style.display = 'flex';
            removeButton.style.display = 'none';
          }
        }
      }
    });
    
    this.updateAddButtonStates();
  }

  updateAddButtonStates() {
    const maxProducts = parseInt(this.dataset.max) || 6;
    const currentCount = this.bundleItems.size;
    const isAtCapacity = currentCount >= maxProducts;
    
    this.querySelectorAll('[data-component="bundle-add-button"]').forEach((buttonWrapper) => {
      const card = buttonWrapper.closest('[data-component="bundle-product-card"]');
      const addButton = buttonWrapper.querySelector('.button');
      const productId = card?.dataset.productId;
      
      if (!addButton) return;

      if (productId === 'placeholder') {
        addButton.disabled = true;
        addButton.setAttribute('aria-disabled', 'true');
        buttonWrapper.classList.add('bundle-add-button--disabled');
        return;
      }

      const isInBundle = this.bundleItems.has(productId);
      
      if (isAtCapacity && !isInBundle) {
        addButton.disabled = true;
        addButton.setAttribute('aria-disabled', 'true');
        addButton.setAttribute('aria-label', 'Bundle limit reached');
        buttonWrapper.classList.add('bundle-add-button--disabled');
      } else {
        addButton.disabled = false;
        addButton.removeAttribute('aria-disabled');
        addButton.setAttribute('aria-label', 'Add to bundle');
        buttonWrapper.classList.remove('bundle-add-button--disabled');
      }
    });
  }

  updateBundleItems(container) {
    const bundleItems = Array.from(this.bundleItems.values());
    const itemElements = container.querySelectorAll('[data-component="bundle-product-item"]');
    const maxVisibleSlots = 2;
    
    itemElements.forEach((itemElement, index) => {
      const bundleItem = bundleItems[index];
      
      if (bundleItem) {
        this.fillBundleItem(itemElement, bundleItem);
        itemElement.classList.remove('hidden');
      } else {
        if (index < maxVisibleSlots) {
          this.showBundleItemPlaceholder(itemElement);
          itemElement.classList.remove('hidden');
        } else {
          itemElement.classList.add('hidden');
        }
      }
    });
  }

  fillBundleItem(itemElement, bundleItem) {
    const mediaElement = itemElement.querySelector('.bundle-product-item__media');
    const contentElement = itemElement.querySelector('.bundle-product-item__content');
    const removeButton = itemElement.querySelector('[data-component="bundle-item-remove"]');
    
    if (mediaElement) {
      const imageUrl = bundleItem.image || bundleItem.productImage || '';
      mediaElement.innerHTML = `
        <img src="${imageUrl}" 
             alt="${bundleItem.title}" 
             class="bundle-product-item__image" 
             width="120" 
             height="120"
             loading="lazy">`;
    }
    
    if (contentElement) {
      let contentHtml = `<h4 class="bundle-product-item__title h6">${bundleItem.title}</h4>`;
      
      if (bundleItem.variantTitle && bundleItem.variantTitle.trim() !== '') {
        contentHtml += `<p class="bundle-product-item__variant" data-component="bundle-item-variant">${bundleItem.variantTitle}</p>`;
      }
      
      const formattedPrice = bundleItem.priceFormatted || this.formatPrice(bundleItem.price);
      contentHtml += `<p class="bundle-product-item__price" data-component="bundle-item-price">${formattedPrice}</p>`;
      
      contentElement.innerHTML = contentHtml;
    }
    
    if (removeButton) {
      removeButton.style.display = 'flex';
      removeButton.setAttribute('data-product-id', bundleItem.productId);
    }
    
    itemElement.dataset.productId = bundleItem.productId;
    itemElement.setAttribute('data-bundle-item', 'true');
    itemElement.classList.remove('bundle-product-item--placeholder');
  }

  showBundleItemPlaceholder(itemElement) {
    const mediaElement = itemElement.querySelector('.bundle-product-item__media');
    const contentElement = itemElement.querySelector('.bundle-product-item__content');
    const removeButton = itemElement.querySelector('[data-component="bundle-item-remove"]');
    
    if (mediaElement) {
      mediaElement.innerHTML = `
        <div class="bundle-product-item__placeholder"> </div>
      `;
    }
    
    if (contentElement) {
      contentElement.innerHTML = `
        <div class="bundle-product-item__skeleton">
          <div class="bundle-product-item__skeleton-line bundle-product-item__skeleton-line--title"></div>
          <div class="bundle-product-item__skeleton-line bundle-product-item__skeleton-line--variant"></div>
          <div class="bundle-product-item__skeleton-line bundle-product-item__skeleton-line--price"></div>
        </div>
      `;
    }
    
    if (removeButton) {
      removeButton.style.display = 'none';
    }
    
    itemElement.dataset.productId = 'placeholder';
    itemElement.classList.add('bundle-product-item--placeholder');
  }

  updateSummary() {
    const summary = this.querySelector('[data-component="bundle-summary"]');
    if (!summary) return;

    const progressBar = summary.querySelector('[data-component="product-bundle-progress-bar"]');
    const itemsContainer = summary.querySelector('[data-bundle-items]');
    const totalPriceElement = summary.querySelector('[data-bundle-total-price]');
    const button = summary.querySelector('.button');

    if (progressBar) {
      const progress = Math.min((this.bundleItems.size / this.minProducts) * 100, 100);
      progressBar.style.setProperty('--progress', `${progress}%`);
    }

    if (button) {
      if (this.bundleItems.size < this.minProducts) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    }

    if (itemsContainer) {
      this.updateBundleItems(itemsContainer);
    }

    if (totalPriceElement) {
      const totalPrice = Array.from(this.bundleItems.values()).reduce((sum, item) => {
        const itemPrice = parseFloat(item.price) || 0;
        return sum + itemPrice;
      }, 0);
      totalPriceElement.textContent = this.formatPrice(totalPrice);
    }
  }

  toggleSummaryVisibility() {
    const summary = this.querySelector('.product-bundle__summary');
    if (!summary) return;

    summary.classList.toggle('bundle-hidden');
  }

  formatPrice(price) {
    const validPrice = parseFloat(price) || 0;
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = this.getCurrencyLocale(currency) || document.documentElement.lang || 'en-US';
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(validPrice / 100);

    if (this.currencyCodeEnabled && !formatted.includes(currency)) {
      return `${formatted} ${currency}`;
    }

    return formatted;
  }

  getCurrencyLocale(currency) {
    const localeMap = {
      USD: 'en-US',
      EUR: 'de-DE',
      GBP: 'en-GB',
      CAD: 'en-CA',
      AUD: 'en-AU',
      NZD: 'en-NZ',
      CHF: 'de-CH',
      SEK: 'sv-SE',
      NOK: 'nb-NO',
      DKK: 'da-DK',
      PLN: 'pl-PL',
      CZK: 'cs-CZ',
      HUF: 'hu-HU',
      RON: 'ro-RO',
      BGN: 'bg-BG',
      TRY: 'tr-TR',
      UAH: 'uk-UA'
    };

    return localeMap[currency] || null;
  }

  saveToStorage() {
    try {
      const data = Array.from(this.bundleItems.entries());
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving bundle to storage:', error);
    }
  }

  loadFromStorage() {
    try {
      const data = sessionStorage.getItem(this.storageKey);
      if (data) {
        const items = JSON.parse(data);
        this.bundleItems = new Map(items);
        this.updateAllCardStates();
      }
    } catch (error) {
      console.error('Error loading bundle from storage:', error);
    }
  }

  refreshBundleItemsFromVariants() {
    if (this.bundleItems.size === 0) return;

    let hasUpdates = false;

    this.bundleItems.forEach((item, key) => {
      const productId = item.productId || key;
      const variantId = item.variantId;

      if (!productId || !variantId) return;

      const variantsScript = document.querySelector(`[data-product-variants="${productId}"]`);
      if (!variantsScript) return;

      try {
        const variantsData = JSON.parse(variantsScript.textContent);
        const variant = variantsData.variants.find((entry) => entry.id === variantId);

        if (!variant) return;

        const variantTitle = variant.title && variant.title !== 'Default Title' && variant.title.trim() !== ''
          ? variant.title
          : '';

        const updatedItem = {
          ...item,
          price: variant.price,
          priceFormatted: variant.price_formatted || item.priceFormatted || null,
          variantTitle: variantTitle,
          image: variant.featured_image || item.productImage || item.image,
          available: variant.available
        };

        this.bundleItems.set(key, updatedItem);
        hasUpdates = true;
      } catch (error) {
        console.error('Error refreshing bundle item price:', error);
      }
    });

    if (hasUpdates) {
      this.updateSummary();
      this.saveToStorage();
    }
  }


  getBundleItems() {
    return Array.from(this.bundleItems.values());
  }

  getBundleCount() {
    return this.bundleItems.size;
  }

  getBundleTotal() {
    return Array.from(this.bundleItems.values()).reduce((sum, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      return sum + itemPrice;
    }, 0);
  }

  clearBundle() {
    this.bundleItems.clear();
    this.updateSummary();
    this.saveToStorage();
    this.updateAllCardStates();
  }
}

customElements.define('product-bundle', ProductBundle);